import { TFunction } from 'i18next';
import { QuestionFormData } from 'pages/leadGen/LeadGenInterface';
import { Control, FieldValues, UseFormTrigger, UseFormWatch } from 'react-hook-form';
import { StepConfigItem } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';

export interface CarInformation {
  uvc: string;
  model_year: string;
  make: string;
  model: string;
  series: string;
  style: string;
  description: string;
}

export interface IStepper {
  step: number;
  setStep: (step: number) => void;
  widgetType: 'embedded' | 'popup';
}

export interface FormData {
  year: string;
  make: string;
  model: string;
  trim: string;
  mileage: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  province: string;
}

export interface OtpFormData {
  confirmMobile: string;
  otp: string;
}

export interface IReqSendOtp {
  phoneNumber: string;
}

export interface IReqVerifyOtp {
  phoneNumber: string;
  otp: string;
}

export interface IReqAuthenticateClient {
  token: string;
  domain: string;
}

export interface Swatch {
  swatch_list: string[];
}

export interface Color extends Swatch {
  name: string;
}

export interface ColorCategory {
  name: string;
  color_list: Color[];
}

export interface ILeadCreationError {
  message?: string;
  name?: string;
  stack?: string;
}

export interface ITradeInState {
  stepCount: number;
  loading: boolean;
  vehicleAutoCompleteData: CarInformation[];
  leadCreationData: { message?: string; success?: boolean };
  updateTradeInData: object;
  sendOtpData: VerificationResponse;
  verifyOtpData: VerifyOtpVerificationResponse;
  vehicleColorData: ColorCategory[];
  publicLeadCreationData: { message?: string; success?: boolean; error?: ILeadCreationError };
  cbbData: any;
  vehicleOptions: any;
  proveData: any;
  proveLoading: boolean;
  proveVerificationData: any;
}

// Send OTP Interface

export interface SendCodeAttempt {
  attempt_sid: string;
  channel: string;
  time: string; // This should be a date, but you can adjust the type as needed
}

export interface Lookup {
  carrier: any; // You can replace 'any' with a more specific type if available
}

export interface Message {
  sid: string;
  serviceSid: string;
  accountSid: string;
  to: string;
  channel: string;
  status: string;
  valid: boolean;
  lookup: Lookup;
  amount: null; // You can replace 'null' with a specific type if available
  payee: null; // You can replace 'null' with a specific type if available
  sendCodeAttempts: SendCodeAttempt[];
  dateCreated: string; // This should be a date, but you can adjust the type as needed
  dateUpdated: string; // This should be a date, but you can adjust the type as needed
  url: string;
}

export interface VerificationResponse {
  success?: boolean;
  message?: Message | string;
}

export interface VerifyOtpMessage {
  sid: string;
  serviceSid: string;
  accountSid: string;
  to: string;
  channel: string;
  status: string;
  valid: boolean;
  amount: null; // You can replace 'null' with a specific type if available
  payee: null; // You can replace 'null' with a specific type if available
  dateCreated: string; // This should be a date, but you can adjust the type as needed
  dateUpdated: string; // This should be a date, but you can adjust the type as needed
}

export interface VerifyOtpVerificationResponse {
  success?: boolean;
  message?: Message | string;
}

export interface LeadDetails {
  Odometer?: string;
  questions?: QuestionFormData[];
}

export interface IReqLeadCreation {
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
  year?: string; // You might want to change the type based on your actual data type
  make?: string;
  model?: string;
  trim?: string;
  leadDetails?: LeadDetails;
}

export interface IReqGoogleReview {
  place_id: string;
  limit?: number;
}

export interface RenderColorListProps {
  colorList: Color[];
  control: Control<any>;
  trigger: UseFormTrigger<any>;
  name: string;
  t: TFunction;
}

export interface Question {
  id: number | string;
  label: string;
  labelTxt?: string;
  required?: boolean;
  type?: 'input' | 'radio' | 'checkbox' | 'select' | 'textarea' | 'number';
  options?: { value: string; label: string }[];
  conditionalQuestions?: { [key: string]: Question[] };
  parentValue?: string;
}

export interface TradeinHeaderProps {
  title: string;
  currentStep?: number;
  stepConfig?: StepConfigItem[];
}

export interface TradeinV3HeaderProps {
  title: string;
  currentStep?: number;
  stepNumber?: number;
  type: string;
  version: string;
}

export interface RenderQuestionParams {
  question: Question;
  control: Control<FieldValues>;
  trigger: UseFormTrigger<FieldValues>;
  t: TFunction;
  watch: UseFormWatch<FieldValues>;
}

export interface RenderQuestionsRecursivelyParams {
  questions: Question[];
  control: Control<FieldValues>;
  trigger: UseFormTrigger<FieldValues>;
  watchAllFields: { [key: string]: any };
  t: TFunction;
  watch: UseFormWatch<FieldValues>;
}

export type Answer = {
  questionid: number;
  answerText: string;
  answerCode: string;
  showChildren: boolean;
};

export type BBQuestion = {
  questionid: number;
  parentid: number;
  questionText: string;
  helpText: string;
  inputType: string;
  required: boolean;
  answers: Answer[];
};

export type TransformedOption = {
  value: string;
  label: string;
};

export type TransformedQuestion = {
  id: number;
  label: string;
  labelTxt: string;
  required: boolean;
  options: TransformedOption[];
};

export enum Flowtype {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
}
